"use client";

import type { FC, Ref } from "react";
import { FieldSet } from "@huub-web/ui/components/FieldSet";

import type { FormInputTemplateProps } from "../FormInputTemplate";
import { TemplatesParser } from "../../components";
import type { TemplateCollection } from "../../types";
import { useVisibilityMap } from "../../hooks/useInputVisibility";

type FieldSetTemplateInput = Omit<FormInputTemplateProps, "onVisibilityChange">;
type FieldSetTemplateInputs = (
  | FieldSetTemplateInput
  | FieldSetTemplateInput[]
)[];

export interface FieldSetTemplateProps {
  /**
   * The unique identifier of the field set (used for visibility tracking)
   */
  id?: string;
  /**
   * The title of the field set
   */
  title?: string;
  /**
   * Will render an avatar with initials if provided
   */
  name?: string;
  /**
   * The inputs to render in the field set
   * @deprecated Use `templates` instead.
   */
  inputs?: FieldSetTemplateInputs;
  /**
   * The templates to render in the field set
   */
  templates?: TemplateCollection;
  /**
   * The description of the field set
   */
  description?: string;
}

/**
 * Extract all form input IDs from a template collection recursively.
 */
const extractFormInputIds = (templates: TemplateCollection): string[] => {
  const ids: string[] = [];

  for (const template of templates) {
    if (template.templateKey === "formInput" && template.id) {
      ids.push(template.id);
    }
    // Check for nested templates in fieldSet, formRowGroup, etc.
    if (template.data?.templates) {
      ids.push(...extractFormInputIds(template.data.templates));
    }
  }

  return ids;
};

export const FieldSetTemplate: FC<
  FieldSetTemplateProps & { ref?: Ref<HTMLFieldSetElement> }
> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  id: _id,
  title,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  inputs: _inputs,
  templates,
  description,
  ref,
}) => {
  const visibilityMap = useVisibilityMap();

  /**
   * Get all form input IDs that belong to this fieldset.
   */
  const fieldSetInputIDs = templates ? extractFormInputIds(templates) : [];

  /**
   * Determine if any inputs in this fieldset are visible.
   * - If there are form inputs in this fieldset, check their visibility in the map
   * - If no form inputs, show the fieldset if it has any templates
   */
  const areAnyVisible = (() => {
    if (fieldSetInputIDs.length === 0) {
      // No form inputs - show fieldset if it has any templates
      return templates && templates.length > 0;
    }

    // Check if any of this fieldset's inputs are visible
    const registeredIDs = fieldSetInputIDs.filter((id) => id in visibilityMap);

    if (registeredIDs.length === 0) {
      // No inputs have registered yet - show fieldset if it has templates
      return templates && templates.length > 0;
    }

    return registeredIDs.some((id) => visibilityMap[id]);
  })();

  return (
    <FieldSet
      ref={ref}
      title={title}
      description={description}
      style={{
        display: areAnyVisible ? "flex" : "none",
      }}
    >
      {templates && templates.length > 0 && (
        <TemplatesParser templates={templates} level={1} />
      )}
    </FieldSet>
  );
};
